/**
 * Die Kachelreihe nach docs/kpi-cockpit-plan.md §2.6.
 *
 * Jede Kachel sagt in einer Zeile, WAS sie zaehlt. Zwei Kacheln mit derselben
 * Beschriftung und verschiedenen Zahlen waren der teuerste Befund des Audits
 * (Nr. 5) — die Definitionszeile ist deshalb Teil der Kachel, nicht Beiwerk.
 *
 * Keine KI-Kosten, keine Abrechnungsquote, keine Ausschoepfungs-Ringe: alle
 * drei verglichen Jahres- mit Lebenszeitwerten.
 */
import React from 'react';
import {
    CheckCircle2, Clock3, Layers, PiggyBank, Receipt, TrendingUp, Wallet,
} from 'lucide-react';
import type { CockpitTiles } from '../../services/cockpitTypes';
import { Bar } from './CockpitBits';
import { fmtCents, pctOf } from './cockpitUi';

interface TileProps {
    caption: string;
    definition: string;
    value: string;
    icon: React.ReactNode;
    accent?: string;
    extra?: React.ReactNode;
}

const Tile: React.FC<TileProps> = ({ caption, definition, value, icon, accent = 'text-slate-400', extra }) => (
    <div data-glass="cockpit-kachel" className="bg-white p-3 rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-1">
            <span className={`text-[11px] font-semibold uppercase tracking-wide ${accent}`}>{caption}</span>
            <span className="shrink-0">{icon}</span>
        </div>
        <div className="text-xl font-semibold text-slate-800 truncate">{value}</div>
        <div className="text-[11px] text-slate-400 mt-1 leading-snug">{definition}</div>
        {extra && <div className="mt-2">{extra}</div>}
    </div>
);

export interface KpiTilesProps {
    tiles: CockpitTiles;
    periodText: string;
    hideAmounts: boolean;
    /**
     * Erbracht im Vormonat — nur bei Zeitraum „Monat" gesetzt. Bei Januar ist
     * das der Dezember des Vorjahres (`model.prevDecember`), nicht der Dezember
     * desselben Jahres: genau dieser Kurzschluss war Befund 10 des Audits.
     */
    prevPerformedCents: number | null;
}

export const KpiTiles: React.FC<KpiTilesProps> = ({ tiles, periodText, hideAmounts, prevPerformedCents }) => {
    const money = (c: number) => fmtCents(c, hideAmounts);
    const realisation = tiles.realisationPct;
    const prevDelta = prevPerformedCents !== null && prevPerformedCents > 0
        ? pctOf(tiles.performedCents - prevPerformedCents, prevPerformedCents)
        : null;

    return (
        <div className="space-y-2">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
                <Tile
                    caption={`Erbracht ${periodText}`}
                    definition="Leistung × aktueller LV-Preis, nach Leistungsdatum"
                    value={money(tiles.performedCents)}
                    accent="text-emerald-600"
                    icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                    extra={
                        <div className="space-y-0.5">
                            <div className="text-[11px] text-slate-400">{tiles.inspections} Begehungen</div>
                            {prevPerformedCents !== null && (
                                <div className="text-[11px] text-slate-400">
                                    Vormonat: {money(prevPerformedCents)}
                                    {prevDelta !== null && (
                                        <span className={prevDelta >= 0 ? ' text-emerald-600' : ' text-red-500'}>
                                            {' '}{prevDelta >= 0 ? '+' : ''}{prevDelta} %
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    }
                />
                <Tile
                    caption={`Abgerechnet ${periodText}`}
                    definition="Netto aus Belegen, Storno negativ"
                    value={money(tiles.billedCents)}
                    accent="text-blue-600"
                    icon={<Receipt className="w-3.5 h-3.5 text-blue-500" />}
                />
                <Tile
                    caption="Noch abzurechnen"
                    definition="Offene Mengen × Preis, Stichtag heute, alle Projekte außer Planung"
                    value={money(tiles.unbilledCents)}
                    icon={<Clock3 className="w-3.5 h-3.5 text-amber-500" />}
                />
                <Tile
                    caption="Offene Forderungen"
                    definition="Gestellte Belege abzüglich Zahlungen, Stichtag heute"
                    value={money(tiles.receivablesOpenCents)}
                    icon={<Wallet className="w-3.5 h-3.5 text-orange-500" />}
                    extra={
                        <div className={`text-[11px] ${tiles.receivablesOverdueCents > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                            davon überfällig: {money(tiles.receivablesOverdueCents)} / {tiles.receivablesOverdueCount} Belege
                        </div>
                    }
                />
                <Tile
                    caption={`Forecast ${periodText}`}
                    definition="Geplante Leistung aus Rhythmus und Restmenge"
                    value={money(tiles.forecastCents)}
                    accent="text-indigo-600"
                    icon={<TrendingUp className="w-3.5 h-3.5 text-indigo-500" />}
                    extra={
                        <div className="space-y-1">
                            <div className="text-[11px] text-slate-500">
                                Realisierung {realisation === null ? '—' : `${Math.round(realisation)} %`}
                            </div>
                            <Bar
                                value={Math.min(100, realisation ?? 0)}
                                max={100}
                                color={(realisation ?? 0) >= 80 ? 'bg-emerald-500' : (realisation ?? 0) >= 50 ? 'bg-amber-400' : 'bg-red-400'}
                                height="h-1"
                            />
                        </div>
                    }
                />
                <Tile
                    caption="Auftragsbestand"
                    definition="Beauftragt minus erbracht, aktive Projekte, Stichtag"
                    value={money(tiles.backlogCents)}
                    icon={<PiggyBank className="w-3.5 h-3.5 text-slate-500" />}
                />
            </div>
            <div data-glass="auf-fenster" className="flex items-center gap-2 text-[11px] text-slate-500 px-1">
                <Layers className="w-3.5 h-3.5 text-sky-500" />
                <span>Pipeline (in Planung): <span className="font-semibold text-slate-700">{money(tiles.pipelineCents)}</span></span>
            </div>
        </div>
    );
};
